// eslint-disable-next-line object-curly-newline
import { ReactNode, memo, useEffect } from "react";
import { createUseStyles } from "react-jss";
import clsx from "clsx";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useDispatch } from "react-redux";
import { AppBar, Sidebar, Text, Breadcrumb } from "$components";

import useAuth from "$hooks/useAuth";
import { setFilter } from "../actions/app";
import { theme } from "$utils/theme/bataillog";

type Props = {
  children?: ReactNode;
  className?: string;
  contentClassName?: string;
  isHeaderRight?: boolean;
  renderHead?: () => ReactNode;
  title?: string;
  titleCustom?: ReactNode;
  backDropLoader?: boolean;
  titlePicto?: () => ReactNode;
  isPublic?: boolean;
};

function Layout({
  children,
  contentClassName = "",
  className = "",
  isHeaderRight,
  renderHead = () => "",
  title = "",
  titleCustom,
  backDropLoader = false,
  titlePicto,
  isPublic = false,
  ...component
}: Props) {
  const classes = useStyles();
  const { isLoggedIn, loading, isAdmin, isContributor, user } = useAuth();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isAdmin() && !isContributor() && user?.userInfo?.userCompany) {
      dispatch(setFilter(user.userInfo.userCompany));
    }
  }, [
    isLoggedIn,
    user?.userInfo?.usercompany,
    dispatch,
    isAdmin,
    isContributor,
    user,
  ]);

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 200);
  }, []);

  return (
    <>
      <div className={clsx(classes.Container, className)} {...component}>
        {!loading && !isPublic && <Sidebar />}
        <AppBar isPublic={isPublic} />
        <div
          className={clsx(
            !isPublic ? classes.content : classes.publicContent,
            contentClassName
          )}
        >
          {!isPublic ? (
            <>
              <Breadcrumb title={title} />

              {(titleCustom || title !== "") && (
                <div
                  className={`${classes.header} ${
                    isHeaderRight ? classes.spaceAround : ""
                  }`}
                >
                  {titleCustom || (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {titlePicto && (
                        <div style={{ marginRight: 8 }}>{titlePicto()}</div>
                      )}
                      <Text type="h2" className={classes.title}>
                        {title}
                      </Text>
                    </div>
                  )}
                  {renderHead()}
                </div>
              )}

              {children}
            </>
          ) : (
            children
          )}
        </div>
      </div>
      <Backdrop className={classes.backdrop} open={backDropLoader}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}

const useStyles = createUseStyles(() => ({
  Container: {
    height: "var(--fullVh)",
    display: "flex",
    flexDirection: "column",
    fontFamily: theme.font.workSans,
  },
  content: {
    padding: [63, 20, 20, 238],
    background: theme.color.gray1,
    flex: 1,
  },
  publicContent: {
    padding: [63, 20, 0, 0],
    background: theme.color.gray1,
    flex: 1,
  },
  header: {
    flex: "initial",
    display: "flex",
    alignItems: "center",
  },
  title: {
    marginTop: 15,
    color: theme.color.blue,
  },
  spaceAround: {
    justifyContent: "space-between",
  },
  backdrop: {
    zIndex: "100000 !important",
    color: "#fff",
  },
}));

export default memo(Layout);
